import { $insertList } from '@lexical/list';
import { List } from 'immutable';
import { $getRoot, $selectAll } from 'lexical';
import {
  getTextStylesFromBrandStyle,
  BrandColorRef,
  tryToGetBrandColorRefByHEX,
  verifyBrandColorRefValueHEX,
} from 'modules/BrandDefinition';
import { TextHorizontalAlignmentType } from 'const';
import * as Models from 'models';
import { $isCustomListItemNode, CustomListItemNode } from '../../nodes/CustomListItemNode';
import { $isCustomListNode, CustomListNode } from '../../nodes/CustomListNode';
import { $removeCustomListAndKeepStyles } from './utils';

export function $verifyStyleForList(
  colors: Models.BrandColorsList,
): void {
  $getRoot().getChildren()
    .filter(node => $isCustomListNode(node))
    .forEach((node: CustomListNode) => {
      const nodeColor = node.getColor();
      if (nodeColor) {
        node.setColor(verifyBrandColorRefValueHEX(colors, nodeColor));
      }
    });
}

const updateNodeStyles = (listItem: CustomListItemNode, size: number, lineHeight: number): void => {
  listItem.setFontSize(size);
  listItem.setLineHeight(lineHeight);
};

function $updateListRecursively(
  list: CustomListNode,
  color: BrandColorRef | undefined,
  size: number,
  lineHeight: number,
): void {
  list.setColor(color);
  const listItems = list.getChildren();
  for (const listItem of listItems) {
    if (!$isCustomListItemNode(listItem)) {
      return;
    }
    updateNodeStyles(listItem, size, lineHeight);
    const nestedLists = listItem.getChildren().filter($isCustomListNode);
    nestedLists.forEach((nestedList) => {
      $updateListRecursively(nestedList, undefined, size, lineHeight);
    });
  }
}

export const $applyBrandStyleForList = (
  brandStyle: Models.BrandStyleMap | undefined,
  colors: Models.BrandColorsList,
): void => {
  const styles = getTextStylesFromBrandStyle(brandStyle, colors, List([]));
  // Bullet color is defined either in the brand definition or in the style list file.
  // If this value includes a reference, it will be replaced by the corresponding hex value on the back-end side
  const bulletColorHex = styles[Models.TextBrandStyleField.BULLET_COLOR];
  const textAlign = styles[Models.TextBrandStyleField.TEXT_ALIGN];
  const size = styles[Models.TextBrandStyleField.FONT_SIZE];
  const lineHeight = styles[Models.TextBrandStyleField.LINE_HEIGHT];
  const color = tryToGetBrandColorRefByHEX(colors, bulletColorHex);

  const shouldConvertToList = textAlign === TextHorizontalAlignmentType.UNORDERED_LIST;
  if (shouldConvertToList) {
    let children = $getRoot().getChildren();
    const onlyLists = children.every($isCustomListNode);
    if (!onlyLists) {
      $selectAll();
      $insertList('bullet');
      children = $getRoot().getChildren();
    }
    children.filter($isCustomListNode).forEach((list) => {
      $updateListRecursively(list, color, size, lineHeight);
    });

    return;
  }

  const hasListAlready = $getRoot().getChildren().some($isCustomListNode);
  if (hasListAlready) {
    $selectAll();
    $removeCustomListAndKeepStyles();
  }
};
